// =============================================================================
// Report period resolution - presets to concrete dates
//
// The one implementation of how a named report preset becomes dates. The
// backend resolves a request's preset with it; the Bill range control uses it
// to preview each preset's range, and the tour harness uses it to pin them.
//
// Every function is pure: "today" is a parameter, never a clock read, so the
// output is deterministic from the inputs. The preset resolvers read only the
// UTC calendar fields of `today`, matching the ledger's UTC date model
// (`@db.Date` columns normalised to `${date}T00:00:00.000Z`), and do all
// calendar arithmetic in UTC to avoid DST off-by-one errors.
//
// `latestCompletedMonth` is the one exception: it reads `today` in SAST
// (Africa/Johannesburg, UTC+2). The preset resolvers' `last-month` and
// `end-of-last-month` still derive the month from the UTC fields, so for the
// two hours after SAST midnight on the 1st the two disagree about which month
// "last month" is. This is accepted: callers converge with the frontend, which
// normalises dates to the browser's local day before they reach a resolver.
// Moving the preset resolvers to SAST would be its own deliberate change.
// =============================================================================
/** Format a UTC Date as a YYYY-MM-DD string (UTC calendar day). */
function toDateString(date) {
    return date.toISOString().slice(0, 10);
}
/**
 * Returns the first day of the financial year containing `asOf`, as a UTC Date:
 * the first day of the month after the financial-year-end month, at or before
 * `asOf`.
 *
 * Worked examples (all UTC):
 *   FYE Feb (month 2) + asOf 2026-06-30 → 2026-03-01 (FY started 1 Mar 2026)
 *   FYE Dec (month 12) + asOf 2026-06-30 → 2026-01-01 (FY started 1 Jan 2026)
 *   FYE Feb (month 2) + asOf 2026-02-15 → 2025-03-01 (still in FY that started 1 Mar 2025)
 *
 * @param financialYearEndMonth  1–12; the month number in which the FY ends.
 * @param asOf                   The report date (UTC).
 */
export function financialYearStart(financialYearEndMonth, asOf) {
    // 1-based, wraps Dec → Jan.
    const fyStartMonth = (financialYearEndMonth % 12) + 1;
    const asOfYear = asOf.getUTCFullYear();
    const asOfMonth = asOf.getUTCMonth() + 1;
    // The FY started this calendar year if its start month has been reached,
    // otherwise last year.
    const fyStartYear = asOfMonth >= fyStartMonth ? asOfYear : asOfYear - 1;
    return new Date(Date.UTC(fyStartYear, fyStartMonth - 1, 1));
}
/**
 * Resolves "Last 12 months" to an inclusive `{ from, to }` window: `to` is
 * today, `from` is the day after the same calendar day one year earlier, so the
 * span is at most 366 inclusive days (under the general-ledger report's
 * 366-day cap). Never financial-year-dependent.
 *
 * `Date.UTC` normalises an overflowing day-of-month rather than clamping it,
 * which keeps month-ends correct (today 31 March gives `from` 1 April of the
 * prior year). 29 February is the one case where the gap is two days: today
 * 2028-02-29 gives `Date.UTC(2027, 1, 30)`, which rolls to 2027-03-02.
 */
export function resolveLast12MonthsRange(today) {
    const from = new Date(Date.UTC(today.getUTCFullYear() - 1, today.getUTCMonth(), today.getUTCDate() + 1));
    return { from: toDateString(from), to: toDateString(today) };
}
/**
 * Resolve a period preset to an inclusive `{ from, to }` window of YYYY-MM-DD
 * strings, or `null` when the preset depends on the financial year and the
 * ledger has no `financialYearEndMonth`.
 *
 * The four FY-dependent presets (`this-financial-year`, `last-financial-year`,
 * `this-quarter`, `last-quarter`) return `null` when `financialYearEndMonth`
 * is `null`; every other preset ignores it and always resolves. A quarter is a
 * quarter of the financial year, not a calendar quarter.
 *
 * @param preset                The named period.
 * @param today                 Reference date (caller-supplied); only its UTC
 *                              calendar fields are read.
 * @param financialYearEndMonth 1-12, or `null` when no financial year end is set.
 */
export function resolvePeriodPreset(preset, today, financialYearEndMonth) {
    const y = today.getUTCFullYear();
    const m = today.getUTCMonth(); // 0-based
    const d = today.getUTCDate();
    switch (preset) {
        case 'this-month': {
            const from = new Date(Date.UTC(y, m, 1));
            const to = new Date(Date.UTC(y, m, d));
            return { from: toDateString(from), to: toDateString(to) };
        }
        case 'last-month': {
            // Day 0 of the current month is the last day of the previous one.
            const from = new Date(Date.UTC(y, m - 1, 1));
            const to = new Date(Date.UTC(y, m, 0));
            return { from: toDateString(from), to: toDateString(to) };
        }
        case 'this-quarter': {
            if (financialYearEndMonth === null)
                return null;
            const fyStart = financialYearStart(financialYearEndMonth, today);
            const monthsSinceFyStart = (y - fyStart.getUTCFullYear()) * 12 + (m - fyStart.getUTCMonth());
            const index = Math.floor(monthsSinceFyStart / 3);
            // Date.UTC month overflow handles index*3 pushing past December.
            const from = new Date(Date.UTC(fyStart.getUTCFullYear(), fyStart.getUTCMonth() + index * 3, 1));
            const to = new Date(Date.UTC(y, m, d));
            return { from: toDateString(from), to: toDateString(to) };
        }
        case 'last-quarter': {
            if (financialYearEndMonth === null)
                return null;
            const fyStart = financialYearStart(financialYearEndMonth, today);
            const monthsSinceFyStart = (y - fyStart.getUTCFullYear()) * 12 + (m - fyStart.getUTCMonth());
            const index = Math.floor(monthsSinceFyStart / 3);
            // Date.UTC month underflow handles crossing the calendar year-end when
            // today is in FY-Q1.
            const from = new Date(Date.UTC(fyStart.getUTCFullYear(), fyStart.getUTCMonth() + (index - 1) * 3, 1));
            // Day 0 of this quarter's start month is the last day of the prior quarter.
            const to = new Date(Date.UTC(fyStart.getUTCFullYear(), fyStart.getUTCMonth() + index * 3, 0));
            return { from: toDateString(from), to: toDateString(to) };
        }
        case 'this-financial-year': {
            if (financialYearEndMonth === null)
                return null;
            const from = financialYearStart(financialYearEndMonth, today);
            const to = new Date(Date.UTC(y, m, d));
            return { from: toDateString(from), to: toDateString(to) };
        }
        case 'last-financial-year': {
            if (financialYearEndMonth === null)
                return null;
            const currentFyStart = financialYearStart(financialYearEndMonth, today);
            const from = new Date(Date.UTC(currentFyStart.getUTCFullYear() - 1, currentFyStart.getUTCMonth(), currentFyStart.getUTCDate()));
            // The prior FY ends the day before the current one starts.
            const to = new Date(currentFyStart);
            to.setUTCDate(to.getUTCDate() - 1);
            return { from: toDateString(from), to: toDateString(to) };
        }
        case 'last-12-months': {
            return resolveLast12MonthsRange(today);
        }
    }
}
/**
 * Resolve an as-at preset to a single YYYY-MM-DD string, or `null` when the
 * preset depends on the financial year and the ledger has no
 * `financialYearEndMonth`.
 *
 * `end-of-last-financial-year` is the day before the current financial year
 * starts, so it agrees with the balance-sheet current-year-earnings split
 * boundary. `today` and `end-of-last-month` ignore `financialYearEndMonth`.
 *
 * @param preset                The named as-at preset.
 * @param today                 Reference date (caller-supplied); only its UTC
 *                              calendar fields are read.
 * @param financialYearEndMonth 1-12, or `null` when no financial year end is set.
 */
export function resolveAsOfPreset(preset, today, financialYearEndMonth) {
    const y = today.getUTCFullYear();
    const m = today.getUTCMonth(); // 0-based
    const d = today.getUTCDate();
    switch (preset) {
        case 'today': {
            return toDateString(new Date(Date.UTC(y, m, d)));
        }
        case 'end-of-last-month': {
            return toDateString(new Date(Date.UTC(y, m, 0)));
        }
        case 'end-of-last-financial-year': {
            if (financialYearEndMonth === null)
                return null;
            const currentFyStart = financialYearStart(financialYearEndMonth, today);
            const endOfLastFy = new Date(currentFyStart);
            endOfLastFy.setUTCDate(endOfLastFy.getUTCDate() - 1);
            return toDateString(endOfLastFy);
        }
    }
}
/** The `{ year, month }` (1-based) that `instant` falls in on the SAST calendar. */
function sastCalendarMonth(instant) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Africa/Johannesburg',
        year: 'numeric',
        month: '2-digit',
    }).formatToParts(instant);
    return {
        year: Number(parts.find((part) => part.type === 'year')?.value),
        month: Number(parts.find((part) => part.type === 'month')?.value),
    };
}
/**
 * The latest calendar month (`YYYY-MM`) that has completely elapsed as at
 * `today`: the month before `today`'s SAST calendar month. The current month is
 * never selectable, because a partial month beside complete ones silently
 * understates it.
 *
 * Reads `today` in the books' own timezone (Africa/Johannesburg, UTC+2, no
 * daylight saving), not UTC. It is a wall-clock instant, not a stored calendar
 * date: a UTC read would still call August "completed" for the first two hours
 * after SAST midnight on 1 September, refusing the month that just closed in
 * the books' own jurisdiction. This is also the default for a period-set
 * request that names no month, which is why a client that pins the clock has to
 * resolve it the same way.
 */
export function latestCompletedMonth(today) {
    const { year, month } = sastCalendarMonth(today);
    // `month` is 1-based and `Date.UTC`'s is 0-based, so `month - 2` is the month
    // BEFORE the current one; Date.UTC handles the underflow in January.
    return new Date(Date.UTC(year, month - 2, 1)).toISOString().slice(0, 7);
}
